import { useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { format, isValid } from 'date-fns'
import { Play, Loader2, Copy, Download, CheckCircle, Star, FileText } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { queryKeys } from '@/lib/query-client'
import {
  getDeployedExtractor,
  getPhenotypeUploads,
  runExtractor,
  type ExtractorRunResult,
  type PhenotypeUpload,
} from '@/api/extractors'

interface ExtractorPlaygroundProps {
  phenotypeId: string
}

export function ExtractorPlayground({ phenotypeId }: ExtractorPlaygroundProps) {
  const [selectedUpload, setSelectedUpload] = useState<string | null>(null)
  const [runResult, setRunResult] = useState<ExtractorRunResult | null>(null)
  const [copied, setCopied] = useState(false)

  // Fetch deployed extractor
  const { data: deployed, isLoading: extractorLoading, error: extractorError } = useQuery({
    queryKey: queryKeys.extractors.deployed(phenotypeId),
    queryFn: () => getDeployedExtractor(phenotypeId),
    retry: false,
  })

  // Fetch uploads for this phenotype
  const { data: uploads, isLoading: uploadsLoading } = useQuery({
    queryKey: queryKeys.extractors.uploads(phenotypeId),
    queryFn: () => getPhenotypeUploads(phenotypeId),
  })

  // Run mutation
  const { mutate: run, isPending: isRunning } = useMutation({
    mutationFn: (uploadId: string) => runExtractor(phenotypeId, uploadId),
    onSuccess: (data) => setRunResult(data),
  })

  // No extractor available
  if (!extractorLoading && (extractorError || !deployed)) {
    return null // Don't render if no extractor exists
  }

  if (extractorLoading || uploadsLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="space-y-4">
            <div className="h-4 w-48 bg-muted animate-pulse rounded" />
            <div className="h-4 w-32 bg-muted animate-pulse rounded" />
          </div>
        </CardContent>
      </Card>
    )
  }

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleDownload = (data: unknown, filename: string) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold">Extractor Playground</h3>
          {deployed && (
            <Badge variant="outline" className="text-xs">
              {deployed.source === 'phenotype_extractors'
                ? `v${deployed.extractor?.version} (deployed)`
                : 'from code gen job'}
            </Badge>
          )}
        </div>
      </div>

      {/* Upload selector */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Run Against Upload</CardTitle>
        </CardHeader>
        <CardContent>
          {uploads && uploads.length > 0 ? (
            <div className="space-y-2">
              {uploads.map((upload: PhenotypeUpload) => (
                <UploadRow
                  key={upload.id}
                  upload={upload}
                  selected={selectedUpload === upload.uploadId}
                  onSelect={() => setSelectedUpload(upload.uploadId)}
                />
              ))}
              <div className="pt-3">
                <Button
                  onClick={() => selectedUpload && run(selectedUpload)}
                  disabled={!selectedUpload || isRunning}
                >
                  {isRunning ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Play className="mr-2 h-4 w-4" />
                  )}
                  Run Extractor
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No uploads found for this phenotype.</p>
          )}
        </CardContent>
      </Card>

      {/* Results */}
      {runResult && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between">
              <span>Extraction Results</span>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-xs">
                  {runResult.railsExtractedData.ratesCount} rates
                </Badge>
                <Badge variant="secondary" className="text-xs">
                  {runResult.railsExtractedData.surchargesCount} surcharges
                </Badge>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Action buttons */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCopy(JSON.stringify(runResult.railsExtractedData, null, 2))}
                >
                  {copied ? (
                    <CheckCircle className="mr-2 h-3 w-3 text-green-500" />
                  ) : (
                    <Copy className="mr-2 h-3 w-3" />
                  )}
                  {copied ? 'Copied!' : 'Copy JSON'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    handleDownload(
                      runResult.railsExtractedData,
                      `extraction-${runResult.uploadId}.json`
                    )
                  }
                >
                  <Download className="mr-2 h-3 w-3" />
                  Download
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    handleCopy(JSON.stringify(runResult.input, null, 2))
                  }
                >
                  <FileText className="mr-2 h-3 w-3" />
                  Copy Input
                </Button>
              </div>

              {/* Rates table preview */}
              {runResult.railsExtractedData.rates.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Rates (first 10)</h4>
                  <div className="overflow-auto max-h-[400px] rounded border">
                    <table className="w-full text-xs">
                      <thead className="bg-muted sticky top-0">
                        <tr>
                          {Object.keys(runResult.railsExtractedData.rates[0]).slice(0, 8).map(key => (
                            <th key={key} className="px-2 py-1.5 text-left font-medium whitespace-nowrap">
                              {key}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {runResult.railsExtractedData.rates.slice(0, 10).map((rate, i) => (
                          <tr key={i} className="border-t">
                            {Object.values(rate).slice(0, 8).map((val, j) => (
                              <td key={j} className="px-2 py-1 whitespace-nowrap">
                                {String(val ?? '')}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {runResult.railsExtractedData.rates.length > 10 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      ... and {runResult.railsExtractedData.rates.length - 10} more rates
                    </p>
                  )}
                </div>
              )}

              {/* Surcharges table preview */}
              {runResult.railsExtractedData.surcharges.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Surcharges</h4>
                  <div className="overflow-auto max-h-[300px] rounded border">
                    <table className="w-full text-xs">
                      <thead className="bg-muted sticky top-0">
                        <tr>
                          {Object.keys(runResult.railsExtractedData.surcharges[0]).slice(0, 8).map(key => (
                            <th key={key} className="px-2 py-1.5 text-left font-medium whitespace-nowrap">
                              {key}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {runResult.railsExtractedData.surcharges.map((surcharge, i) => (
                          <tr key={i} className="border-t">
                            {Object.values(surcharge).slice(0, 8).map((val, j) => (
                              <td key={j} className="px-2 py-1 whitespace-nowrap">
                                {String(val ?? '')}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Full JSON (collapsible) */}
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                  View raw JSON
                </summary>
                <pre className="mt-2 p-3 bg-muted rounded-md overflow-auto max-h-[400px] whitespace-pre-wrap">
                  {JSON.stringify(runResult.railsExtractedData, null, 2)}
                </pre>
              </details>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

function UploadRow({
  upload,
  selected,
  onSelect,
}: {
  upload: PhenotypeUpload
  selected: boolean
  onSelect: () => void
}) {
  const d = new Date(upload.createdAt)
  const dateStr = isValid(d) ? format(d, 'MMM d, HH:mm') : ''

  return (
    <button
      onClick={onSelect}
      className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${
        selected
          ? 'bg-primary/10 border border-primary/30'
          : 'bg-muted/50 hover:bg-muted border border-transparent'
      }`}
    >
      <div className="flex items-center gap-3">
        <div
          className={`h-3 w-3 rounded-full border-2 ${
            selected ? 'border-primary bg-primary' : 'border-muted-foreground'
          }`}
        />
        <span className="font-mono text-xs truncate max-w-[300px]">{upload.uploadId}</span>
        {upload.isTrainingExample && (
          <Badge variant="outline" className="text-xs gap-1">
            <Star className="h-3 w-3" />
            Example
          </Badge>
        )}
      </div>
      <span className="text-xs text-muted-foreground">{dateStr}</span>
    </button>
  )
}
